import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Drawer, Layout } from 'antd';
import classnames from 'classnames';

import { toggleCollapsedSideNav, updateWindowWidth } from 'appRedux/actions/Setting';
import { get, set } from 'packages/utils/sessionStorage';
import {
  NAV_STYLE_DRAWER,
  NAV_STYLE_FIXED,
  NAV_STYLE_MINI_SIDEBAR,
  NAV_STYLE_NO_HEADER_EXPANDED_SIDEBAR,
  NAV_STYLE_NO_HEADER_MINI_SIDEBAR,
  TAB_SIZE,
} from '../../constants/ThemeSetting';

import { SIDEBAR_COLLAPSED_STORAGE_KEY } from './constants';
import styles from './styles.module.less';
import SideBarMenu from './SideBarMenu';
import WorkDirectionMenu from './WorkDirectionMenu';
import { subscribeWindowWidth } from './utils/subscribeWindowWidth';

const { Sider } = Layout;

const Sidebar = ({ isWorkDirecton = false }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const { navStyle } = useSelector(({ settings }) => settings);
  const { navCollapsed, width } = useSelector(({ common }) => common);
  const isDrawerNav = navStyle === NAV_STYLE_DRAWER || width < TAB_SIZE;
  const isMiniSidebarStyle =
    navStyle === NAV_STYLE_MINI_SIDEBAR || navStyle === NAV_STYLE_NO_HEADER_MINI_SIDEBAR;
  const previousPathname = useRef(pathname);
  const [railCollapsedOverride, setRailCollapsedOverride] = useState(() => {
    const storedState = get(SIDEBAR_COLLAPSED_STORAGE_KEY, null);
    if (typeof storedState === 'boolean') return storedState;
    return typeof storedState?.collapsed === 'boolean' ? storedState.collapsed : null;
  });
  // The rail is width-only: antd's `collapsed` would put the Menu in popup mode and
  // relayout every row, which is exactly the jump the rail is meant to avoid.
  const isSiderCollapsed =
    !isDrawerNav && (railCollapsedOverride ?? isMiniSidebarStyle);

  const onToggleCollapsedNav = () => {
    dispatch(toggleCollapsedSideNav(!navCollapsed));
  };

  const onToggleRail = useCallback(() => {
    const next = !isSiderCollapsed;
    set(SIDEBAR_COLLAPSED_STORAGE_KEY, { collapsed: next });
    setRailCollapsedOverride(next);
  }, [isSiderCollapsed]);

  useEffect(
    () => subscribeWindowWidth(nextWidth => dispatch(updateWindowWidth(nextWidth))),
    [dispatch],
  );

  useEffect(() => {
    if (previousPathname.current === pathname) {
      return;
    }
    previousPathname.current = pathname;
    if (isDrawerNav && navCollapsed) {
      dispatch(toggleCollapsedSideNav(false));
    }
  }, [dispatch, isDrawerNav, navCollapsed, pathname]);

  let drawerStyle = 'gx-collapsed-sidebar';

  if (navStyle === NAV_STYLE_FIXED) {
    drawerStyle = '';
  } else if (navStyle === NAV_STYLE_NO_HEADER_MINI_SIDEBAR) {
    drawerStyle = 'gx-mini-sidebar gx-mini-custom-sidebar';
  } else if (navStyle === NAV_STYLE_NO_HEADER_EXPANDED_SIDEBAR) {
    drawerStyle = 'gx-custom-sidebar';
  } else if (navStyle === NAV_STYLE_MINI_SIDEBAR) {
    drawerStyle = 'gx-mini-sidebar';
  } else if (navStyle === NAV_STYLE_DRAWER) {
    drawerStyle = 'gx-collapsed-sidebar';
  }
  if (
    (navStyle === NAV_STYLE_FIXED ||
      navStyle === NAV_STYLE_MINI_SIDEBAR ||
      navStyle === NAV_STYLE_NO_HEADER_EXPANDED_SIDEBAR) &&
    width < TAB_SIZE
  ) {
    drawerStyle = 'gx-collapsed-sidebar';
  }

  const renderSiderBarMenu = (inDrawer = false) => {
    if (isWorkDirecton) {
      return <WorkDirectionMenu />;
    }
    return (
      <SideBarMenu
        collapsed={isSiderCollapsed}
        isDrawerNav={inDrawer}
        onToggleCollapse={onToggleRail}
      />
    );
  };

  return (
    <Sider
      className={classnames(
        'gx-app-sidebar',
        drawerStyle,
        styles.sidebar,
        isSiderCollapsed && styles.sidebarRail,
      )}
      trigger={null}
    >
      {isDrawerNav ? (
        <Drawer
          className={classnames('gx-drawer-sidebar', styles.drawerRoot)}
          closable={false}
          mask={false}
          onClose={onToggleCollapsedNav}
          placement="left"
          open={navCollapsed}
          zIndex={1}
        >
          {renderSiderBarMenu(true)}
        </Drawer>
      ) : (
        renderSiderBarMenu()
      )}
    </Sider>
  );
};
export default Sidebar;
